import React, { useState, useEffect } from 'react'
import { FiSearch, FiUserPlus, FiUserMinus, FiUsers, FiMapPin, FiPhone, FiMail } from 'react-icons/fi'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import api, { fetchAll } from '../../utils/api'
import Spinner from '../../components/Spinner'
import { ErrorNotification } from '../../utils'
import { canModifyActivity } from '../../utils/activityUtils'

const FarmerActivityModal = ({ activity, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFarmers, setSelectedFarmers] = useState([])
  const [selectedAssignedFarmers, setSelectedAssignedFarmers] = useState([])
  const [loading, setLoading] = useState(false)
  const readOnly = !canModifyActivity(activity)

  // Fetch farmers using the same pattern as FarmerView
  const [farmers, setFarmers] = useState([])
  const [activityFarmers, setActivityFarmers] = useState([])
  const [farmersLoading, setFarmersLoading] = useState(false)

  const normalizeFarmerList = (data) => {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.results)) return data.results
    if (Array.isArray(data?.farmers)) return data.farmers
    return []
  }

  // Fetch all farmers (paginated API)
  const getAllFarmers = async () => {
    setFarmersLoading(true)
    try {
      const data = await fetchAll('farmers/')
      setFarmers(normalizeFarmerList(data))
    } catch (error) {
      ErrorNotification(error.response?.data || error.message || 'Failed to get farmers. Please try again')
      setFarmers([])
    } finally {
      setFarmersLoading(false)
    }
  }

  // Fetch activity farmers
  const getActivityFarmers = async () => {
    if (!activity?.id) {
      setActivityFarmers([])
      return
    }

    try {
      const response = await api.get(`activities/${activity.id}/farmers/`)
      setActivityFarmers(normalizeFarmerList(response?.data))
    } catch (error) {
      ErrorNotification(error.response?.data || error.message || 'Failed to fetch activity farmers')
      setActivityFarmers([])
    }
  }

  useEffect(() => {
    getAllFarmers()
    getActivityFarmers()
  }, [activity?.id])

  // Ensure activityFarmers is always an array
  const safeActivityFarmers = Array.isArray(activityFarmers) ? activityFarmers : []

  // Filter farmers based on search term and exclude already assigned farmers (same pattern as FarmerView)
  const assignedFarmerIds = safeActivityFarmers?.map(af => af.farmer?.id || af.id) || []
  const filteredFarmers = Array.isArray(farmers) ? farmers?.filter(farmer => {
    // Filter by search term (same logic as FarmerView)
    const matchesSearch = searchTerm && searchTerm.trim() ? (
      farmer?.firstname?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      farmer?.lastname?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      farmer?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      farmer?.phone?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    ) : true

    // Exclude already assigned farmers
    const notAssigned = !assignedFarmerIds.includes(farmer?.id)

    return matchesSearch && notAssigned
  }) || [] : []

  const handleAssignFarmer = async (farmer) => {
    if (readOnly) return
    setLoading(true)
    try {
      const response = await api.post(`activities/${activity?.id}/add_farmers/`, {
        farmer_ids: [farmer?.id]
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${farmer?.firstname} ${farmer?.lastname} assigned to activity`)
        getActivityFarmers() // Refresh the activity farmers list
        getAllFarmers() // Refresh available farmers list
      }
    } catch (error) {
      ErrorNotification(error.response.data || error.message || 'Failed to assign farmer to activity')
    } finally {
      setLoading(false)
    }
  }

  const handleUnassignFarmer = async (farmer) => {
    if (readOnly) return
    setLoading(true)
    try {
      const response = await api.post(`activities/${activity?.id}/remove_farmers/`, {
        farmer_ids: [farmer?.id]
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${farmer?.firstname} ${farmer?.lastname} removed from activity`)
        getActivityFarmers() // Refresh the activity farmers list
        getAllFarmers() // Refresh available farmers list
      }
    } catch (error) {
      ErrorNotification(error.response.data || error.message || 'Failed to remove farmer from activity')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkAssign = async () => {
    if (readOnly) return
    if (selectedFarmers.length === 0) {
      ErrorNotification('Please select farmers to assign')
      return
    }

    setLoading(true)
    try {
      const farmerIds = selectedFarmers.map(farmer => farmer?.id)
      const response = await api.post(`activities/${activity?.id}/add_farmers/`, {
        farmer_ids: farmerIds
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${selectedFarmers.length} farmers assigned to activity`)
        setSelectedFarmers([])
        getActivityFarmers() // Refresh the activity farmers list
        getAllFarmers() // Refresh available farmers list
      }
    } catch (error) {
      ErrorNotification(error.response.data || error.message || 'Failed to assign farmers to activity')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectFarmer = (farmer) => {
    setSelectedFarmers(prev =>
      prev.find(f => f.id === farmer?.id)
        ? prev.filter(f => f.id !== farmer?.id)
        : [...prev, farmer]
    )
  }

  const handleSelectAll = () => {
    if (selectedFarmers.length === filteredFarmers.length) {
      setSelectedFarmers([])
    } else {
      setSelectedFarmers(filteredFarmers)
    }
  }

  const handleBulkRemove = async (farmersToRemove) => {
    if (readOnly) return
    if (farmersToRemove.length === 0) {
      ErrorNotification('Please select farmers to remove')
      return
    }

    setLoading(true)
    try {
      const farmerIds = farmersToRemove.map(farmer => farmer?.id)
      const response = await api.post(`activities/${activity?.id}/remove_farmers/`, {
        farmer_ids: farmerIds
      })
      if (response?.status === 200 || response?.status === 201) {
        toast.success(`${farmersToRemove.length} farmers removed from activity`)
        getActivityFarmers() // Refresh the activity farmers list
        getAllFarmers() // Refresh available farmers list
      }
    } catch (error) {
      ErrorNotification(error.response.data || error.message || 'Failed to remove farmers from activity')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAssignedFarmer = (farmer) => {
    setSelectedAssignedFarmers(prev =>
      prev.find(f => f.id === farmer?.id)
        ? prev.filter(f => f.id !== farmer?.id)
        : [...prev, farmer]
    )
  }

  const handleSelectAllAssigned = () => {
    if (selectedAssignedFarmers.length === safeActivityFarmers.length) {
      setSelectedAssignedFarmers([])
    } else {
      setSelectedAssignedFarmers(safeActivityFarmers.map(af => af.farmer || af))
    }
  }

  if (!activity?.id) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-6xl relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <div>
            <h2 className='text-xl font-bold dark:text-white'>
              Manage Farmers - {activity?.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Assign and manage farmers for this activity
            </p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {readOnly && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
            This activity has ended. Farmer assignments can no longer be changed.
          </div>
        )}

        {farmersLoading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="flex h-full">
            {/* Available Farmers */}
            <div className="flex-1 p-6 border-r border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Available Farmers ({filteredFarmers.length})
                </h3>
                {filteredFarmers.length > 0 && !readOnly && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {selectedFarmers.length === filteredFarmers.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedFarmers.length > 0 && (
                      <button
                        onClick={handleBulkAssign}
                        disabled={loading}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loading ? 'Assigning...' : `Assign ${selectedFarmers.length}`}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search farmers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredFarmers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'No farmers found matching your search' : 'No available farmers to assign'}
                  </div>
                ) : (
                  filteredFarmers.map(farmer => (
                    <div
                      key={farmer?.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedFarmers.find(f => f.id === farmer?.id)
                        ? 'border-yellowcolor bg-yellowcolor/10 dark:bg-yellowcolor/20'
                        : 'border-gray-200 dark:border-border-dark hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      onClick={() => handleSelectFarmer(farmer)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {farmer?.firstname} {farmer?.lastname}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <div className="flex items-center">
                              <FiMail className="w-4 h-4 mr-1" />
                              {farmer?.email}
                            </div>
                            <div className="flex items-center">
                              <FiPhone className="w-4 h-4 mr-1" />
                              {farmer?.phone}
                            </div>
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <FiMapPin className="w-4 h-4 mr-1" />
                            {farmer?.region}, {farmer?.district}
                          </div>
                        </div>
                        {!readOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAssignFarmer(farmer)
                          }}
                          disabled={loading}
                          className="ml-2 p-2 text-yellowcolor hover:bg-yellowcolor/10 dark:hover:bg-yellowcolor/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <FiUserPlus className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Assigned Farmers */}
            <div className="flex-1 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Assigned Farmers ({safeActivityFarmers.length})
                </h3>
                {safeActivityFarmers.length > 0 && !readOnly && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAllAssigned}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      {selectedAssignedFarmers.length === safeActivityFarmers.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedAssignedFarmers.length > 0 && (
                      <button
                        onClick={() => handleBulkRemove(selectedAssignedFarmers)}
                        disabled={loading}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        {loading ? 'Removing...' : `Remove ${selectedAssignedFarmers.length}`}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {safeActivityFarmers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <FiUsers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No farmers assigned to this activity</p>
                  </div>
                ) : (
                    safeActivityFarmers.map(activityFarmer => {
                      const farmer = activityFarmer.farmer || activityFarmer
                      const isSelected = selectedAssignedFarmers.find(f => f.id === farmer?.id)
                    return (
                      <div
                        key={farmer?.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-200 dark:border-border-dark hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        onClick={() => handleSelectAssignedFarmer(farmer)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {farmer?.firstname} {farmer?.lastname}
                            </h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                              <div className="flex items-center">
                                <FiMail className="w-4 h-4 mr-1" />
                                {farmer?.email}
                              </div>
                              <div className="flex items-center">
                                <FiPhone className="w-4 h-4 mr-1" />
                                {farmer?.phone}
                              </div>
                            </div>
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                              <FiMapPin className="w-4 h-4 mr-1" />
                              {farmer?.region}, {farmer?.district}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Assigned: {new Date(activityFarmer.assigned_at).toLocaleDateString()}
                            </div>
                          </div>
                          {!readOnly && (
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleUnassignFarmer(farmer)
                              }}
                              disabled={loading}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <FiUserMinus className="w-4 h-4" />
                            </button>
                          </div>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default FarmerActivityModal
